import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import logger from '../utils/logger';

const poolData = {
  UserPoolId: process.env.REACT_APP_AWS_USER_POOL_ID,
  ClientId: process.env.REACT_APP_AWS_CLIENT_ID
};

const userPool = new CognitoUserPool(poolData);

export const signUp = (email, password, name, mobile) => {
  return new Promise((resolve, reject) => {
    // Clean and format mobile number for Cognito
    // Cognito expects E.164 format: +61404726690
    const cleanMobile = mobile.replace(/\s|\(|\)|-/g, '');
    let formattedMobile = cleanMobile;
    
    // Convert to international format if needed
    if (cleanMobile.startsWith('0') && cleanMobile.length === 10) {
      formattedMobile = '+61' + cleanMobile.substring(1);
    } else if (cleanMobile.startsWith('61') && cleanMobile.length === 11) {
      formattedMobile = '+' + cleanMobile;
    } else if (!cleanMobile.startsWith('+')) {
      formattedMobile = '+61' + cleanMobile;
    }
     

    const attributeList = [
      {
        Name: 'name',
        Value: name
      },
      {
        Name: 'phone_number',
        Value: formattedMobile
      }
    ];

    userPool.signUp(email, password, attributeList, null, (err, result) => {
      if (err) {
        logger.error('Cognito signup error:', err);
        reject(err);
        return;
      }
      logger.log('Cognito signup success:', result);
      resolve(result);
    });
  });
};

export const signIn = (email, password) => {
  return new Promise((resolve, reject) => {
    const authenticationData = {
      Username: email,
      Password: password
    };
    
    const authenticationDetails = new AuthenticationDetails(authenticationData);
    
    const userData = {
      Username: email,
      Pool: userPool
    };
    
    const cognitoUser = new CognitoUser(userData);
    
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        resolve(result);
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

export const signOut = () => {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
};

export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    
    if (!cognitoUser) {
      reject(new Error('No current user'));
      return;
    }
    
    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      
      if (!session.isValid()) {
        reject(new Error('Invalid session'));
        return;
      }
      
      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }
        
        const userAttributes = {};
        attributes.forEach(attr => {
          userAttributes[attr.getName()] = attr.getValue();
        });
        
        resolve({
          id: cognitoUser.getUsername(),
          email: userAttributes.email,
          name: userAttributes.name,
          session: session
        });
      });
    });
  });
};

export const confirmSignUp = (email, code) => {
  return new Promise((resolve, reject) => {
    const userData = {
      Username: email,
      Pool: userPool
    };
    
    const cognitoUser = new CognitoUser(userData);
    
    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

export const resendConfirmationCode = (email) => {
  return new Promise((resolve, reject) => {
    const userData = {
      Username: email,
      Pool: userPool
    };
    
    const cognitoUser = new CognitoUser(userData);
    
    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

// Forgot password - sends reset code to user's email
export const forgotPassword = (email) => {
  return new Promise((resolve, reject) => {
    const userData = {
      Username: email,
      Pool: userPool
    };
    
    const cognitoUser = new CognitoUser(userData);
    
    cognitoUser.forgotPassword({
      onSuccess: (result) => {
        logger.log('Forgot password code sent:', result);
        resolve(result);
      },
      onFailure: (err) => {
        logger.error('Forgot password error:', err);
        reject(err);
      }
    });
  });
};

// Confirm forgot password - reset password with code
export const confirmForgotPassword = (email, code, newPassword) => {
  return new Promise((resolve, reject) => {
    const userData = {
      Username: email,
      Pool: userPool
    };
    
    const cognitoUser = new CognitoUser(userData);
    
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        logger.log('Password reset successful');
        resolve({ success: true });
      },
      onFailure: (err) => {
        logger.error('Confirm forgot password error:', err);
        reject(err);
      }
    });
  });
};

export { userPool };
